import {Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, CanActivate, NavigationEnd, Router, RouterStateSnapshot} from "@angular/router";
import {AuthService} from "../../services/auth.service";
import {UserInfoService} from "../../services/user-info.service";
import {ConsentService} from "../../services/consent.service";
import {getPathWithoutQueryAndHash} from "../utils/url.utils";
import {catchError, filter, map, switchMap, take, timeout} from "rxjs/operators";
import {combineLatest, from, Observable, of} from "rxjs";
import {AuthenticateService} from "../../services/authenticate.service";
import {SessionStorageService} from "../../services/session-storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate {
  private readonly biometricsEnrollPath = "/login/biometrics-enroll";

  constructor(
    public authService: AuthService,
    public router: Router,
    public userService: UserInfoService,
    private consentService: ConsentService,
    private authenticateService: AuthenticateService,
    private sessionStorageService: SessionStorageService,
  ) {

    //TODO: Maybe not the best place to initialize and subscribe to it.
    this.initializeMainRouteConsentCheck();
  }

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot
  ): Observable<boolean> {

    return combineLatest([
      this.authService.isSessionInitialized$(),
      this.authService.isNew$
    ]).pipe(
      take(1),
      switchMap(([sessionInitialized, isNew]) => {
        const targetUrl = _state?.url;
        const path = getPathWithoutQueryAndHash(targetUrl ?? "");

        if (path === this.biometricsEnrollPath) {
          const allowEnrollment =
            sessionInitialized
            && !isNew
            && this.sessionStorageService.hasBiometricEnrollmentRequired();
          if (allowEnrollment) {
            return of(true);
          }
          this.router.navigate(["/welcome"]);
          return of(false);
        }

        if (this.sessionStorageService.hasBiometricEnrollmentRequired() && !isNew && sessionInitialized) {
          this.router.navigate([this.biometricsEnrollPath]);
          return of(false);
        }

        // In consent callback flow from NT, /contact-info can be opened before in-memory
        // session state is fully rehydrated. Without this, the guard may redirect too early to /welcome page
        // and the post-consent finalization flow never gets a chance to run and finalize.
        if (targetUrl === "/contact-info" && !sessionInitialized) {
          // Use getSessionContext(false) to refresh session data WITHOUT auto-redirecting to start page.
          // We only want to stabilize state for this route first.
          return from(this.authenticateService.getSessionContext(false)).pipe(
            switchMap(() =>
              this.authService.isSessionInitialized$().pipe(
                filter(initialized => initialized === true),
                take(1),
                // Prevent infinite waiting. If initialization does not complete in time,
                // we fail safely by redirecting to welcome.
                timeout(3000),
                map(() => {
                  const allow = !isNew;
                  if (allow) {
                    return true;
                  }

                  this.router.navigate(["/welcome"]);
                  return false;
                }),
                catchError(_error => {
                  this.router.navigate(["/welcome"]);
                  return of(false);
                }),
              ),
            ),
            catchError(_error => {
              this.router.navigate(["/welcome"]);
              return of(false);
            }),
          );
        }

        // Default behavior for all other routes: session must be initialized and user not marked new.
        const allow = sessionInitialized && !isNew;
        if (allow) {
          return of(true);
        }

        this.router.navigate(["/welcome"]);
        return of(false);
      })
    )
  }

  /**
   * Initializes the main route consent check.
   * method subscribes to the router events and checks if the current route is the main route.
   * If user has not given consent, it checks for the consents from "Nõusolekuteenus".
   *
   * @return {void}
   */
  private initializeMainRouteConsentCheck(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      filter(_ => this.isMainRoute(this.router.url)),
    ).subscribe(async _ => {
      let insuranceConsentReference = Boolean(this.userService.sessionContext.getValue().termsOutput.insuranceConsentReference);
      this.consentService.updateConsent(!!insuranceConsentReference);
      if (!this.consentService.userHasGivenConsent) {
        await this.consentService.checkIfHasNtConsents();
      }
    });
  }

  private isMainRoute(url: string): boolean {
    return getPathWithoutQueryAndHash(url) === "/";
  }
}
